import { FC, useState } from "react";
import { Card, Row, Col, List, Radio, Badge } from "antd";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { ColProps } from "antd/lib/col";
import { useLocale } from "src/locales";
import { useSelector } from "react-redux";

type DataType = "company" | "brand";

interface Values {
  name: {
    ko_KR: string;
    en_US: string;
  };
  value: number;
}

interface Data {
  company: Values[];
  brand: Values[];
}

const data: Data = {
  company: [
    { name: { ko_KR: "골프존", en_US: "GolfZone" }, value: 544 },
    { name: { ko_KR: "SSG랜더스", en_US: "SSG Landers" }, value: 321 },
    { name: { ko_KR: "카카오", en_US: "KAKAO" }, value: 113 },
    { name: { ko_KR: "나이키", en_US: "NIKE" }, value: 213 },
    { name: { ko_KR: "스타벅스", en_US: "StarBucks" }, value: 83 },
    { name: { ko_KR: "기타", en_US: "others" }, value: 41 },
  ],
  brand: [
    { name: { ko_KR: "골프존-파크", en_US: "GolfZone-Park" }, value: 244 },
    { name: { ko_KR: "SSG랜더스-TGX", en_US: "SSG Landers-TGX" }, value: 231 },
    { name: { ko_KR: "카카오-VX", en_US: "KAKAO-VX" }, value: 311 },
    {
      name: { ko_KR: "나이키-나이키 스포츠", en_US: "Nuke-Nike Sports" },
      value: 41,
    },
    {
      name: { ko_KR: "나이키-나이키 의류", en_US: "Nike-Nike Cloth" },
      value: 121,
    },
    { name: { ko_KR: "기타", en_US: "others" }, value: 111 },
  ],
};

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#E36E7E",
  "#8F66DE",
];

const wrapperCol: ColProps = {
  xs: 24,
  sm: 24,
  md: 12,
  lg: 12,
  xl: 12,
  xxl: 12,
};

const PossessionGraph: FC<{ loading: boolean }> = ({ loading }) => {
  const [dataType, setDataType] = useState<DataType>("company");
  const { locale } = useSelector((state) => state.user);
  const { formatMessage } = useLocale();

  return (
    <Card className="salePercent" bordered={false} loading={loading}>
      <Row gutter={20}>
        <Col {...wrapperCol}>
          <ResponsiveContainer height={250}>
            <PieChart>
              <Tooltip
                content={({ active, payload }: any) => {
                  if (active) {
                    const { name, value } = payload[0];
                    const total = data[dataType]
                      .map((d) => d.value)
                      .reduce((a, b) => a + b);
                    const percent = ((value / total) * 100).toFixed(2) + "%";

                    return (
                      <span className="customTooltip">
                        {name[locale]}({value}) : {percent}
                      </span>
                    );
                  }

                  return null;
                }}
              />
              <Pie
                strokeOpacity={1}
                data={data[dataType]}
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
              >
                {data[dataType].map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index]} />
                ))}
              </Pie>
              <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={14}
                fontWeight="bold"
                fill="#555"
              >
                {formatMessage({ id: "menu.dashboard.circleGraph.title" })}
              </text>
            </PieChart>
          </ResponsiveContainer>
        </Col>
        <Col {...wrapperCol}>
          <div style={{ width: "100%", margin: "0 auto" }}>
            <Radio.Group
              value={dataType}
              onChange={(e) => setDataType(e.target.value)}
              buttonStyle="solid"
              style={{ marginBottom: 12, width: "100%" }}
            >
              <Radio.Button
                value="company"
                style={{ width: "50%", textAlign: "center" }}
              >
                {formatMessage({ id: "menu.dashboard.circleGraph.sector1" })}
              </Radio.Button>
              <Radio.Button
                value="brand"
                style={{ width: "50%", textAlign: "center" }}
              >
                {formatMessage({ id: "menu.dashboard.circleGraph.sector2" })}
              </Radio.Button>
            </Radio.Group>
            <List<Values>
              bordered
              dataSource={data[dataType]}
              renderItem={(item, index) => {
                const total = data[dataType]
                  .map((d) => d.value)
                  .reduce((a, b) => a + b);
                const percent = ((item.value / total) * 100).toFixed(2) + "%";

                return (
                  <List.Item>
                    <Badge color={COLORS[index]} />
                    <span>{item.name[locale]}</span>
                    <span style={{ fontSize: "11px", marginLeft: "-2px" }}>
                      ({item.value})
                    </span>{" "}
                    <span style={{ textAlign: "right" }}>{percent}</span>
                  </List.Item>
                );
              }}
            />
          </div>
        </Col>
      </Row>
    </Card>
  );
};

export default PossessionGraph;
